import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "submitButton",
    "signatureInput",
    "signatureSurface",
    "clearSignatureButton",
    "signatureGateNote",
  ];

  connect() {
    console.log("Staff Signature controller connected");
    if (!this.hasFormTarget || !this.hasSignatureInputTarget) {
      console.warn("staff-signature controller missing required targets");
      return;
    }
    this.bindCompetencyReviewBehavior();
    this.setupFormSubmission();
    this.updateSigningAvailability();
  }

  bindCompetencyReviewBehavior() {
    this.element
      .querySelectorAll(".js-supervisor-rating-radio")
      .forEach((radio) => {
        radio.addEventListener("change", () => {
          this.updateRatingDefinitionForCard(radio.closest(".js-competency-review-card"));
          this.updateSigningAvailability();
        });
      });

    this.element
      .querySelectorAll(".js-remove-competency-toggle")
      .forEach((checkbox) => {
        checkbox.addEventListener("change", () =>
          this.toggleCompetencyRemovalState(checkbox)
        );
        this.toggleCompetencyRemovalState(checkbox);
      });

    this.element
      .querySelectorAll(".js-removal-reason-input")
      .forEach((input) => {
        input.addEventListener("input", () => this.updateSigningAvailability());
        input.addEventListener("change", () => this.updateSigningAvailability());
      });

    this.element
      .querySelectorAll(".js-competency-review-card")
      .forEach((card) => this.updateRatingDefinitionForCard(card));
  }

  toggleCompetencyRemovalState(checkbox) {
    const card = checkbox.closest(".js-competency-review-card");
    if (!card) return;

    const removalWrap = card.querySelector(".js-removal-reason-wrap");
    const removalInput = card.querySelector(".js-removal-reason-input");
    const ratingRadios = card.querySelectorAll(".js-supervisor-rating-radio");
    const isRemoved = checkbox.checked;

    if (removalWrap) {
      removalWrap.classList.toggle("d-none", !isRemoved);
    }

    if (removalInput) {
      removalInput.required = isRemoved;
      if (!isRemoved) {
        removalInput.value = "";
      }
    }

    ratingRadios.forEach((radio) => {
      radio.disabled = isRemoved;
      radio.required = !isRemoved;
      if (isRemoved) {
        radio.checked = false;
      }
    });

    card.classList.toggle("is-removed", isRemoved);
    this.updateRatingDefinitionForCard(card);
    this.updateSigningAvailability();
  }

  updateRatingDefinitionForCard(card) {
    if (!card) return;

    const definitionEl = card.querySelector(".js-rating-definition-output");
    if (!definitionEl) return;

    const removeToggle = card.querySelector(".js-remove-competency-toggle");
    const isRemoved = removeToggle && removeToggle.checked;
    if (isRemoved) {
      definitionEl.classList.add("d-none");
      definitionEl.innerHTML = "";
      return;
    }

    const selectedRating = card.querySelector(".js-supervisor-rating-radio:checked");
    if (!selectedRating) {
      definitionEl.classList.add("d-none");
      definitionEl.innerHTML = "";
      return;
    }

    const short = selectedRating.dataset.short || "";
    const definition = selectedRating.dataset.definition || "";
    definitionEl.innerHTML = "";

    const labelEl = document.createElement("strong");
    labelEl.className = "rating-definition-label";
    labelEl.textContent = short;

    const textEl = document.createElement("p");
    textEl.className = "rating-definition-text";
    textEl.textContent = definition;

    definitionEl.appendChild(labelEl);
    definitionEl.appendChild(textEl);
    definitionEl.classList.remove("d-none");
  }

  setupFormSubmission() {
    this.formTarget.addEventListener("ajax:success", (event) => {
      const [data, status, xhr] = event.detail;
      
      if (data.success) {
        // Redirect to success page
        window.location.href = data.redirect_url;
      }
    });

    this.formTarget.addEventListener("ajax:error", (event) => {
      const [data, status, xhr] = event.detail;
      
      let errorMessage = "Failed to submit signature. Please try again.";
      if (data && data.error) {
        errorMessage = data.error;
      }
      
      alert(errorMessage);
      this.submitButtonTarget.disabled = false;
    });

    this.formTarget.addEventListener("ajax:before", (event) => {
      if (!this.validateCompetencyReviews()) {
        event.preventDefault();
        return;
      }

      // Validate signature
      if (!this.signatureInputTarget.value || this.signatureInputTarget.value.trim() === "") {
        event.preventDefault();
        alert("Please provide your signature before submitting.");
      }
    });
  }

  validateCompetencyReviews() {
    const cards = this.element.querySelectorAll(".js-competency-review-card");

    for (const card of cards) {
      const removeToggle = card.querySelector(".js-remove-competency-toggle");
      const removalInput = card.querySelector(".js-removal-reason-input");
      const isRemoved = removeToggle && removeToggle.checked;

      if (isRemoved) {
        if (!removalInput || !removalInput.value.trim()) {
          alert("Please provide a removal reason for each inapplicable competency.");
          return false;
        }
        continue;
      }

      const selectedRating = card.querySelector(
        ".js-supervisor-rating-radio:checked"
      );
      if (!selectedRating) {
        alert("Please rate every competency before submitting your signature.");
        return false;
      }
    }

    return true;
  }

  onSignatureChanged(event) {
    if (event && event.detail && event.detail.isEmpty) {
      this.signatureInputTarget.value = "";
    }
    this.updateSigningAvailability();
  }

  updateSigningAvailability() {
    const allReviewed = this.allCompetenciesReviewed();
    const hasSignature =
      this.signatureInputTarget.value &&
      this.signatureInputTarget.value.trim() !== "";

    if (this.hasSignatureSurfaceTarget) {
      this.signatureSurfaceTarget.classList.toggle("signature-locked", !allReviewed);
      const canvas = this.signatureSurfaceTarget.querySelector("canvas");
      if (canvas) {
        canvas.style.pointerEvents = allReviewed ? "auto" : "none";
      }
    }

    if (this.hasClearSignatureButtonTarget) {
      this.clearSignatureButtonTarget.disabled = !allReviewed;
    }

    if (this.hasSignatureGateNoteTarget) {
      this.signatureGateNoteTarget.classList.toggle("d-none", allReviewed);
    }

    if (!allReviewed) {
      this.clearSignature();
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !(allReviewed && hasSignature);
    }
  }

  allCompetenciesReviewed() {
    const cards = this.element.querySelectorAll(".js-competency-review-card");
    return Array.from(cards).every((card) => this.competencyReviewed(card));
  }

  competencyReviewed(card) {
    const removeToggle = card.querySelector(".js-remove-competency-toggle");
    const removalInput = card.querySelector(".js-removal-reason-input");
    const isRemoved = removeToggle && removeToggle.checked;

    if (isRemoved) {
      return !!(removalInput && removalInput.value.trim());
    }

    return !!card.querySelector(".js-supervisor-rating-radio:checked");
  }

  clearSignature() {
    if (this.hasSignatureInputTarget) {
      this.signatureInputTarget.value = "";
    }

    if (this.hasSignatureSurfaceTarget) {
      const canvas = this.signatureSurfaceTarget.querySelector("canvas");
      if (canvas) {
        const context = canvas.getContext("2d");
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = "rgb(255, 255, 255)";
        context.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    const signedBanner = document.getElementById("signature-locked-banner");
    if (signedBanner) signedBanner.remove();

    const signedTimestamp = document.getElementById("signature-timestamp");
    if (signedTimestamp) signedTimestamp.remove();
  }
}


